import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    Stack,
    Switch,
    Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { useOutletContext } from 'react-router-dom';

import { getServiceProviderDailyActivity } from 'api/rest/analytics';
import { useAuth } from 'contexts/ProtectedRoute';
import { Role } from 'data/constants/roles';
import useTranslate from 'hooks/useTranslate';
import type { ServiceProviderDailyActivityData } from 'hooks/analytics';

import ProviderActivityTable from './components/ProviderActivityTable';
import { AnalyticsError, AnalyticsEmpty, AnalyticsLoading } from '../../components/AnalyticsState';
import {
    AnalyticsColors,
    SectionCard,
    SectionSubtitle,
    SectionTitle,
} from '../../components/analyticsUi';
import useAnalyticsData from '../../hooks/useAnalyticsData';
import type { AnalyticsOutletContext } from '../../AnalyticsShell';

const REFRESH_INTERVAL_MS = 60_000;
const hasActivityInPeriod = (provider: ServiceProviderDailyActivityData[number]): boolean =>
    provider.totals.recordedActions > 0 || provider.totals.recordedEvents > 0;

const ProviderActivityView: React.FC = () => {
    const { role } = useAuth();
    const { translate } = useTranslate();
    const { dateRangeKey, from, to } = useOutletContext<AnalyticsOutletContext>();
    const isSystemAdmin = role === Role.SUPER_SUPER_ADMIN;
    const [showActiveOnly, setShowActiveOnly] = useState(true);
    const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);
    const [isPageVisible, setIsPageVisible] = useState(
        () => document.visibilityState === 'visible'
    );

    const requestRange = useMemo(() => {
        if (dateRangeKey === 'custom') {
            return from && to ? ({ from, to } as const) : ({ days: 30 } as const);
        }

        return { days: Number(dateRangeKey) as 7 | 30 | 90 } as const;
    }, [dateRangeKey, from, to]);

    const { data, loading, error, refetch } = useAnalyticsData<ServiceProviderDailyActivityData>(
        () => getServiceProviderDailyActivity(requestRange),
        [requestRange]
    );

    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsPageVisible(document.visibilityState === 'visible');
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    useEffect(() => {
        if (!isPageVisible) return undefined;

        const intervalId = window.setInterval(refetch, REFRESH_INTERVAL_MS);
        return () => window.clearInterval(intervalId);
    }, [isPageVisible, refetch]);

    useEffect(() => {
        if (
            selectedProviderId !== null &&
            !data?.some(
                (provider) =>
                    provider.id === selectedProviderId &&
                    (!showActiveOnly || hasActivityInPeriod(provider))
            )
        ) {
            setSelectedProviderId(null);
        }
    }, [data, selectedProviderId, showActiveOnly]);

    if (!isSystemAdmin) {
        return (
            <AnalyticsError
                title={translate('restrictedView')}
                message={translate('providerActivitySystemAdminOnly')}
            />
        );
    }

    if (loading && !data) {
        return <AnalyticsLoading />;
    }

    if (error && !data) {
        return <AnalyticsError message={error} />;
    }

    const providers = data ?? [];
    const visibleProviders = showActiveOnly ? providers.filter(hasActivityInPeriod) : providers;
    const totalRecordedActions = visibleProviders.reduce(
        (total, provider) => total + provider.totals.recordedActions,
        0
    );
    const todayKey = new Date().toISOString().slice(0, 10);
    const hasPartialToday = visibleProviders.some((provider) =>
        provider.daily.some((metric) => metric.day === todayKey)
    );

    return (
        <Box sx={{ px: { xs: 1, sm: 2 }, pb: 3 }}>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                sx={{ alignItems: { xs: 'stretch', md: 'center' }, mb: 2 }}
            >
                <FormControlLabel
                    control={
                        <Switch
                            checked={showActiveOnly}
                            onChange={(event) => setShowActiveOnly(event.target.checked)}
                            size="small"
                        />
                    }
                    label={translate('onlyProvidersWithActivity')}
                    sx={{
                        m: 0,
                        '& .MuiFormControlLabel-label': {
                            fontSize: 13,
                            color: 'text.secondary',
                            whiteSpace: 'nowrap',
                        },
                    }}
                />

                <Button
                    variant="outlined"
                    size="small"
                    startIcon={loading ? <CircularProgress size={15} /> : <RefreshRoundedIcon />}
                    onClick={refetch}
                    disabled={loading}
                    sx={{ alignSelf: { xs: 'stretch', md: 'center' }, textTransform: 'none' }}
                >
                    {translate('refresh')}
                </Button>
            </Stack>

            <SectionCard>
                <SectionTitle>{translate('providerActivity')}</SectionTitle>
                <SectionSubtitle>{translate('providerActivityDescription')}</SectionSubtitle>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1, sm: 3 }}
                    sx={{ alignItems: { xs: 'flex-start', sm: 'center' }, flexWrap: 'wrap' }}
                >
                    <Box>
                        <Typography
                            sx={{
                                color: AnalyticsColors.textTertiary,
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: '0.05em',
                                textTransform: 'uppercase',
                            }}
                        >
                            {translate('providersTracked')}
                        </Typography>
                        <Typography
                            sx={{
                                color: AnalyticsColors.textPrimary,
                                fontSize: 24,
                                fontWeight: 700,
                            }}
                        >
                            {visibleProviders.length.toLocaleString()}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography
                            sx={{
                                color: AnalyticsColors.textTertiary,
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: '0.05em',
                                textTransform: 'uppercase',
                            }}
                        >
                            {translate('totalRecordedActions')}
                        </Typography>
                        <Typography
                            sx={{
                                color: AnalyticsColors.textPrimary,
                                fontSize: 24,
                                fontWeight: 700,
                            }}
                        >
                            {totalRecordedActions.toLocaleString()}
                        </Typography>
                    </Box>
                    {hasPartialToday ? (
                        <Typography
                            sx={{
                                color: AnalyticsColors.primary,
                                fontSize: 12,
                                fontWeight: 700,
                                backgroundColor: AnalyticsColors.primaryLight,
                                borderRadius: 1,
                                px: 1,
                                py: 0.5,
                            }}
                        >
                            {translate('livePartialDay')}
                        </Typography>
                    ) : null}
                </Stack>
            </SectionCard>

            {providers.length === 0 ? (
                <AnalyticsEmpty
                    title={translate('noProviderActivity')}
                    description={translate('noProviderActivityDescription')}
                />
            ) : visibleProviders.length === 0 ? (
                <AnalyticsEmpty
                    title={translate('noActiveProviderActivity')}
                    description={translate('noActiveProviderActivityDescription')}
                />
            ) : (
                <ProviderActivityTable
                    providers={visibleProviders}
                    selectedProviderId={selectedProviderId}
                    onProviderSelect={(providerId) =>
                        setSelectedProviderId((current) =>
                            current === providerId ? null : providerId
                        )
                    }
                />
            )}

            {visibleProviders.length > 0 ? (
                <SectionCard>
                    <SectionTitle>{translate('heatmapLegend')}</SectionTitle>
                    <SectionSubtitle>{translate('heatmapLegendDescription')}</SectionSubtitle>
                    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                        {[0, 0.25, 0.5, 0.75, 1].map((intensity) => (
                            <Box
                                key={intensity}
                                sx={{
                                    width: 18,
                                    height: 18,
                                    borderRadius: '3px',
                                    backgroundColor:
                                        intensity === 0
                                            ? 'rgba(99, 102, 241, 0.06)'
                                            : `rgba(99, 102, 241, ${0.16 + intensity * 0.7})`,
                                }}
                            />
                        ))}
                        <Typography
                            sx={{ color: AnalyticsColors.textSecondary, fontSize: 12, ml: 1 }}
                        >
                            {translate('moreRecordedActions')}
                        </Typography>
                    </Stack>
                </SectionCard>
            ) : null}
        </Box>
    );
};

export default ProviderActivityView;
